import {Component, OnInit, signal} from '@angular/core';
import {Router} from "@angular/router";
import {firstValueFrom} from "rxjs";
import {FhirService} from "../patient/fhir.service";
import {ProcessingService} from "../processing-overlay/processing-service";
import { MarkdownComponent } from 'ngx-markdown';

@Component({
  selector: 'app-eula-overlay',
  imports: [MarkdownComponent],
  templateUrl: './eula-overlay.html',
  styleUrl: './eula-overlay.scss',
})
export class EulaOverlay implements OnInit {

  bypassDaysRemaining = 30;
  eulaFile = 'assets/legal/epic-app-license-agreement-v1.0.md';
  eulaVersion = this.eulaFile.substring(this.eulaFile.lastIndexOf("v") + 1, this.eulaFile.indexOf(".md"));
  protected readonly hasScrolledToBottom = signal(false);

  constructor(private readonly router: Router, private fhirService: FhirService, private processingService: ProcessingService) {}

  ngOnInit(): void {
    console.log('EULA OVERLAY STARTED');
    console.log('Eula version - ', this.eulaVersion);
    // state info comes from OrgGateComponent
    const state = history.state;
    this.bypassDaysRemaining = state.bypassDaysRemaining ?? 30;
    setTimeout(() => {
      this.processingService.hide();
    }, 200);
  }

  protected onScroll(event: Event): void {
    const element = event.target as HTMLElement;
    const threshold = 10;
    const atBottom =
      element.scrollTop + element.clientHeight >=
      element.scrollHeight - threshold;
    if (atBottom) {
      this.hasScrolledToBottom.set(true);
    }
  }

  protected async accept(): Promise<void> {
    try {
      const state = history.state;
      const selectedOrg = state.selectedOrg;
      if (!selectedOrg?.organization) {
        throw new Error('Organization not found in EULA state.');
      }
      const organization = selectedOrg.organization;

      await this.showProcessingMessage('Saving EULA acceptance...');

      const updatedOrganization = this.updateEulaStatus(organization, true);
      await firstValueFrom(this.fhirService.updateOrganization(updatedOrganization,organization.id));
      const updatedSelectedOrg = {...selectedOrg, organization: updatedOrganization};

      await this.showProcessingMessage('EULA accepted. Loading application...');

      setTimeout(() => {
        this.processingService.hide();
      }, 400);

      await this.router.navigate( ['/landing'], {
          state: {
            oktaToken: state.oktaToken,
            decoded: state.decoded,
            selectedOrg: updatedSelectedOrg
          }
        }
      );
    } catch (error) {
      this.processingService.hide();
      console.error('Failed to accept EULA:',error);
    }
  }

  protected async bypass(): Promise<void> {
    try {
      const state = history.state;
      const selectedOrg = state.selectedOrg;
      const eulaAlreadyExists= state.eulaAlreadyExists;

      if (!selectedOrg?.organization) {
        throw new Error('Organization not found in EULA state.');
      }
      const organization = selectedOrg.organization;

      if (!eulaAlreadyExists) {
        this.showProcessingMessage('Saving EULA bypass...');
        await firstValueFrom(
          this.fhirService.updateOrganization(
            organization,
            organization.id
          )
        );
      }
      await this.showProcessingMessage('EULA bypassed. Loading application...');

      setTimeout(() => {
        this.processingService.hide();
      }, 400);

      await this.router.navigate(['/landing'], {
        state: {
          oktaToken: state.oktaToken,
          decoded: state.decoded,
          selectedOrg
        }
      });
    } catch (error) {
      this.processingService.hide();
      console.error('Failed to bypass EULA:', error);
    }
  }

  private updateEulaStatus(organization: any, accepted: boolean): any {
    const updatedOrganization = structuredClone(organization);

    const eulaExtension =
      updatedOrganization.extension?.find(
        (ext: any) =>
          ext.url === 'https://cibmtr.org/fhir/StructureDefinition/cra-eula-status'
      );

    if (!eulaExtension) {
      throw new Error('Cannot update EULA status: EULA extension not found.');
    }

    const acceptedExtension =
      eulaExtension.extension?.find(
        (ext: any) =>
          ext.url === 'accepted'
      );

    if (acceptedExtension) {
      acceptedExtension.valueBoolean = accepted;
    } else {
      eulaExtension.extension.push({
        url: 'accepted',
        valueBoolean: accepted
      });
    }

    if (accepted) {
      const acceptedDateExtension =
        eulaExtension.extension?.find(
          (ext: any) =>
            ext.url === 'acceptedDate'
        );

      if (acceptedDateExtension) {
        acceptedDateExtension.valueDateTime = new Date().toISOString();
      } else {
        eulaExtension.extension.push({
          url: 'acceptedDate',
          valueDateTime: new Date().toISOString()
        });
      }
    }
    return updatedOrganization;
  }

  private async showProcessingMessage(message: string): Promise<void> {
    this.processingService.show(message);
    await new Promise<void>(
      resolve => setTimeout(resolve, 450)
    );
  }
}
